import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { MatIcon } from '@angular/material/icon';
import {
  EVENTQ_EMAIL_INVALID_MESSAGE,
  isEventqEmail,
} from '../../validators/email.validator';
import {
  EVENTQ_REQUIRED_INVALID_MESSAGE,
  isEventqRequired,
} from '../../validators/required.validator';

export type EventqInputType = 'text' | 'email' | 'password';

/** Where the field label sits relative to the control. */
export type EventqInputLabelPosition = 'overlay' | 'above';

@Component({
  selector: 'eventq-input',
  imports: [MatIcon],
  templateUrl: './input.html',
  styleUrl: './input.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[attr.disabled]': 'disabled() ? "" : null',
  },
})
export class EventqInput {
  private static nextId = 0;

  private readonly host = inject(ElementRef<HTMLElement>);
  private readonly destroyRef = inject(DestroyRef);

  readonly inputId = `eventq-input-${EventqInput.nextId++}`;

  readonly label = input<string>('');
  /**
   * Label placement when `label` is non-empty.
   * - `overlay` (default): straddles the top edge of the field (Figma outlined style)
   * - `above`: sits above the field
   */
  readonly labelPosition = input<EventqInputLabelPosition>('overlay');
  readonly placeholder = input<string>('');
  readonly type = input<EventqInputType>('text');
  readonly multiline = input(false);
  readonly rows = input(1);
  readonly requiredIndicator = input(false);
  readonly showPasswordToggle = input(false);
  readonly validateEmail = input<boolean | undefined>(undefined);
  readonly validateRequired = input<boolean | undefined>(undefined);
  readonly submitAttempted = input(false);
  readonly value = input<string>('');
  readonly disabled = input(false);
  /** When set, input and paste are capped at this length. A longer bound value is an error. */
  readonly maxLength = input<number | null>(null);

  readonly valueChange = output<string>();
  readonly validChange = output<boolean>();

  readonly passwordVisible = signal(false);
  readonly touched = signal(false);
  private lastBoundValue: string | null = null;

  constructor() {
    const onScroll = (event: Event): void => this.onAncestorScroll(event);
    document.addEventListener('scroll', onScroll, true);
    this.destroyRef.onDestroy(() => {
      document.removeEventListener('scroll', onScroll, true);
    });

    effect(() => {
      const next = this.value();
      if (this.disabled()) {
        this.touched.set(false);
      } else if (this.lastBoundValue !== null && next !== this.lastBoundValue) {
        this.touched.set(false);
      }
      this.lastBoundValue = next;
    });
  }

  onInput(event: Event): void {
    if (this.disabled() || this.passwordMasked()) {
      return;
    }

    const target = event.target as HTMLInputElement | HTMLTextAreaElement;
    const next = this.capToMaxLength(target.value);
    if (target.value !== next) {
      target.value = next;
    }
    this.valueChange.emit(next);
    this.emitValidity(next);
  }

  onBeforeInput(event: InputEvent): void {
    if (this.disabled() || !this.passwordMasked()) {
      return;
    }

    const input = event.target as HTMLInputElement;
    const current = this.value();
    const start = input.selectionStart ?? current.length;
    const end = input.selectionEnd ?? start;
    let next = current;
    let cursor = start;

    if (event.inputType === 'insertText' && event.data) {
      next = current.slice(0, start) + event.data + current.slice(end);
      cursor = start + event.data.length;
    } else if (event.inputType === 'deleteContentBackward' && start > 0) {
      next = current.slice(0, start - 1) + current.slice(end);
      cursor = start - 1;
    } else if (event.inputType === 'deleteContentForward' && end < current.length) {
      next = current.slice(0, start) + current.slice(end + 1);
      cursor = start;
    } else if (event.inputType === 'insertFromPaste' && event.data) {
      next = current.slice(0, start) + event.data + current.slice(end);
      cursor = start + event.data.length;
    } else {
      return;
    }

    event.preventDefault();
    this.valueChange.emit(next);
    this.emitValidity(next);
    this.restoreMaskedInput(input, next.length, cursor);
  }

  onBlur(): void {
    this.touched.set(true);
    this.emitValidity(this.value());
  }

  displayValue(): string {
    if (this.passwordMasked()) {
      return '*'.repeat(this.value().length);
    }

    return this.value();
  }

  emailValidationEnabled(): boolean {
    const explicit = this.validateEmail();
    return explicit ?? this.type() === 'email';
  }

  requiredValidationEnabled(): boolean {
    const explicit = this.validateRequired();
    return explicit ?? this.type() === 'password';
  }

  showValidationError(): boolean {
    if (this.disabled()) {
      return false;
    }
    if (this.exceedsMaxLength(this.value())) {
      return true;
    }
    return (this.touched() || this.submitAttempted()) && !this.isValid();
  }

  validationMessage(): string {
    const value = this.value();
    const maxLength = this.maxLength();

    if (maxLength != null && value.length > maxLength) {
      return `Limit is ${maxLength} characters`;
    }

    if (this.emailValidationEnabled() && !isEventqEmail(value)) {
      return EVENTQ_EMAIL_INVALID_MESSAGE;
    }

    if (this.requiredValidationEnabled() && !isEventqRequired(value)) {
      return EVENTQ_REQUIRED_INVALID_MESSAGE;
    }

    return '';
  }

  isValid(): boolean {
    return this.computeValidity(this.value());
  }

  togglePasswordVisibility(): void {
    if (this.disabled()) {
      return;
    }
    this.passwordVisible.update((visible) => !visible);
  }

  passwordMasked(): boolean {
    return this.type() === 'password' && !this.passwordVisible();
  }

  resolvedType(): EventqInputType | 'text' {
    return this.type() === 'password' ? 'text' : this.type();
  }

  inputAutocomplete(): string | null {
    switch (this.type()) {
      case 'email':
        return 'email';
      case 'password':
        return 'current-password';
      default:
        return null;
    }
  }

  /**
   * Nested scrollers (the incident-report protocol stack) do not emit `window:scroll`.
   * Blur dismisses the browser suggestion popup. Scrolling the field itself does not.
   */
  private onAncestorScroll(event: Event): void {
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement)) {
      return;
    }
    if (!this.host.nativeElement.contains(active)) {
      return;
    }
    const target = event.target;
    if (!(target instanceof Node) || target === active || active.contains(target)) {
      return;
    }
    if (!target.contains(active)) {
      return;
    }
    active.blur();
  }

  private emitValidity(value: string): void {
    this.validChange.emit(this.computeValidity(value));
  }

  private computeValidity(value: string): boolean {
    if (this.exceedsMaxLength(value)) {
      return false;
    }

    if (this.emailValidationEnabled() && !isEventqEmail(value)) {
      return false;
    }

    if (this.requiredValidationEnabled() && !isEventqRequired(value)) {
      return false;
    }

    return true;
  }

  private exceedsMaxLength(value: string): boolean {
    const maxLength = this.maxLength();
    return maxLength != null && value.length > maxLength;
  }

  private capToMaxLength(value: string): string {
    const maxLength = this.maxLength();
    if (maxLength == null || value.length <= maxLength) {
      return value;
    }

    return value.slice(0, maxLength);
  }

  private restoreMaskedInput(input: HTMLInputElement, length: number, cursor: number): void {
    queueMicrotask(() => {
      input.value = '*'.repeat(length);
      const position = Math.min(Math.max(cursor, 0), length);
      input.setSelectionRange(position, position);
    });
  }
}
